import React, { useEffect, useState } from "react";
import Badge from "../kit/Badge";
import Chip from "../kit/Chip";
import "./EventCard.css";
import { backendUrl } from "../../api/constants";
import { AiFillHeart } from "react-icons/ai";
import { useUser } from "../../context/UserContext/UserProvider";
import { distance, formatDateTime } from "../../util/eventUtils";
import { highlightMatches } from "../../util/styleUtils";
import { useUIContext } from "../../context/UIContext/UIContext";
import { useData } from "../../context/DataContext/DataProvider";
import { loadIcon } from "../../util/iconRegistry";
import { useEventDetails } from "../../context/EventDetailsProvider/EventDetailsProvider";
import { useAccountDetailsSetters } from "../../context/AccountDetailsContext/AccountDetailsProvider";
import { FaEye } from "react-icons/fa6";
import { MdRestaurant, MdLocalBar, MdWbSunny } from "react-icons/md";
import { TbTicket } from "react-icons/tb";

const CardTitle = ({ children }) => {
  return (
    <div
      className="text-kit-lg mb-kit-4 [&>*]:font-['Primary-Medium'] max-w-full leading-[1.9rem] flex justify-between"
      style={{
        color: "var(--inyc-primary-text-color)",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: "2",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
};

const normalizeLocationText = (value = "") =>
  value.trim().toLowerCase().replace(/\s+/g, " ");

const getLegacyGoogleLocationName = (address = "") => {
  const parts = address.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length < 4) return "";

  const [possibleName] = parts;
  return /^\d+\s/.test(normalizeLocationText(possibleName))
    ? ""
    : possibleName;
};

const parseTicketPrice = (value) => {
  if (typeof value !== "string" && typeof value !== "number") return null;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized || normalized === "$" || normalized.includes("donation")) {
    return null;
  }

  const match = normalized.replace(/,/g, "").match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const hasTicketValue = (value) => Boolean(value && value !== "$");

const formatTicketPrice = (ticket) => {
  const priceType = ticket?.priceType || "priced_at";
  const price = hasTicketValue(ticket?.price) ? ticket.price : "";
  const priceMax = hasTicketValue(ticket?.priceMax) ? ticket.priceMax : "";

  switch (priceType) {
    case "starting_at":
      return price ? `starting at ${price}` : null;
    case "donation":
      return price || "donation";
    case "rsvp":
      return price || "rsvp";
    case "door":
      return price ? `${price} at the door` : "at the door";
    case "range":
      return price && priceMax ? `${price} - ${priceMax}` : price || null;
    case "priced_at":
    default:
      return price || null;
  }
};

const isDisplayableTicket = (ticket) =>
  Boolean(
    ticket?.link ||
    ticket?.name ||
    ticket?.description ||
    hasTicketValue(ticket?.price) ||
    hasTicketValue(ticket?.priceMax) ||
    ticket?.priceType === "donation" ||
    ticket?.priceType === "rsvp"
  );

const getTicketBadgeLabelForEvent = (event) => {
  if (!event) return null;

  const tickets = Array.isArray(event.tickets)
    ? event.tickets.filter(isDisplayableTicket)
    : [];
  const hasTicketSignal =
    tickets.length > 0 ||
    event.ticket_link ||
    hasTicketValue(event.ticket_price);

  if (!hasTicketSignal) return null;

  const pricedTickets = tickets
    .map((ticket) => ({
      ticket,
      price: parseTicketPrice(ticket?.price),
    }))
    .filter(({ price }) => Number.isFinite(price))
    .sort((a, b) => a.price - b.price);

  if (pricedTickets.length > 0) {
    const cheapest = pricedTickets[0].ticket;
    const price = cheapest?.price;
    return tickets.length > 1 ? `tickets from ${price}` : `tickets ${formatTicketPrice(cheapest)}`;
  }

  const donationTicket = tickets.find((ticket) => ticket?.priceType === "donation");
  if (donationTicket) return "tickets donation";

  const rsvpTicket = tickets.find((ticket) => ticket?.priceType === "rsvp");
  if (rsvpTicket) return "rsvp";

  if (hasTicketValue(event.ticket_price)) {
    return `tickets ${event.ticket_price}`;
  }

  return "tickets";
};

const getTicketBadgeLabel = (...events) => {
  for (const event of events) {
    const label = getTicketBadgeLabelForEvent(event);
    if (label) return label;
  }

  return null;
};

const EventCard = ({
  data,
  disabled,
  latestEvent,
  className = "default-event-container",
  onClick,
  onVenueClick,
  hideEventCount,
  fullWidth = false,
}) => {
  const [event, setEvent] = useState();
  const { user } = useUser();
  const { likedEvents } = useUIContext();
  const { globalConfig, userLocation } = useData();
  const categories = globalConfig?.categories;
  const knownServices = globalConfig?.knownServices || {};
  const { selectAccount } = useAccountDetailsSetters();
  // const { selectedEvent } = useEventDetails();

  useEffect(() => {
    setEvent(data);
  }, [data]);

  // declare latestEventDate which is latestEvent if it exists, otherwise it is the first of the event's events if it exists
  const latestEventData = event?._venueMatches
    ? event
    : latestEvent || event?.events?.[0] || event;
  const {
    formattedDate: latestEventformattedDate,
    formattedTime: latestEventformattedTime,
    dateString: latestEventDateString,
    rruleString: latestEventRruleString,
  } = formatDateTime(
    latestEventData?.nextOccurence || latestEventData?.date,
    event?.timezone || latestEventData?.timezone,
    {
      rrule: latestEventData?.rrule,
      recurring: latestEventData?.recurring,
      recurringEnd: latestEventData?.recurringEnd,
      frequency: latestEventData?.frequency,
    },
    latestEventData  // Pass event context for debug tracing
  );

  // console.log('timezone for event ', event?.name, ' is ', event?.timezone)

  const vmatches = event?.type === "venue" && event?._venueMatches;

  const likedLatestEvent =
    likedEvents?.find((e) => e._id === latestEventData?._id) || latestEventData;
  const likedEvent = likedEvents?.find((e) => e._id === event?._id) || event;
  const ticketBadgeLabel = getTicketBadgeLabel(latestEventData, event);

  const activeColor = categories?.find(
    (category) => category.id === latestEventData?.category
  )?.activeColor;

  const venueName =
    latestEventData?.venue === event?._id
      ? event?.name
      : event?.venueDetails?._id
        ? event?.venueDetails?.name
        : event?.venue?._id
          ? event?.venue?.name
          : null;
  const googleLocationName =
    !venueName && event?.type !== "venue"
      ? latestEventData?.addressFields?.venueName?.trim() ||
        event?.addressFields?.venueName?.trim() ||
        getLegacyGoogleLocationName(latestEventData?.address || event?.address || "") ||
        ""
      : "";
  const addressLabel = googleLocationName || latestEventData?.address?.toLowerCase();
  const location =
    event?.location ||
    // selectedEvent?.location ||
    event?.venueDetails?.location ||
    latestEventData?.venueDetails?.location;

  // console.log('for event ',event?.name , ' location is ', location)

  //if eventdistance is not a valid number, set it to null
  let eventdistance =
    location?.coordinates &&
    distance(
      userLocation?.lat,
      userLocation?.lng,
      location?.coordinates[1],
      location?.coordinates[0]
    );

  if (isNaN(eventdistance)) {
    eventdistance = null;
  } else {
    eventdistance = eventdistance.toFixed(2);
  }

  const posterAccount = latestEventData?.account_id?._id
    ? latestEventData?.account_id
    : latestEventData?.accountDetails?._id
      ? latestEventData?.accountDetails
      : null;

  return (
    <div
      className={`relative w-full max-w-full overflow-hidden ${fullWidth ? "" : "md:max-w-[600px]"}
      ${disabled ? "opacity-[0.4]" : ""}`}
    >
      {event && (
        <div
          className={`event-container max-w-full overflow-hidden ${className}`}
          onClick={() => {
            onClick && onClick();
          }}
        >


          <div className="w-full max-w-full min-w-0 overflow-hidden flex justify-start gap-kit-16 items-stretch">
            <div className="flex-1 min-w-0 max-w-full overflow-hidden">



              <CardTitle>
                <div
                  dangerouslySetInnerHTML={{
                    __html: highlightMatches(
                      latestEventData?.name,
                      latestEventData?._matches,
                      "name"
                    ),
                  }}
                ></div>
              </CardTitle>


              <div
                className="flex justify-between items-start gap-kit-16"
              >

                {vmatches ? (
                  <div
                    style={{
                      fontSize: "var(--inyc-text-base)",
                      color: "var(--inyc-secondary-medium)",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: highlightMatches(
                        latestEventData?.address?.toLowerCase(),
                        latestEventData?._matches,
                        "address"
                      ),
                    }}
                  ></div>
                ) : (
                  <div
                    className=""
                    style={{
                      color: "var(--inyc-secondary-medium)",
                    }}
                  >
                    {event?.type === "venue" && !venueName && event?.name}

                    {/* {latestEventData?.address?.toLowerCase()} */}
                    {venueName || googleLocationName ? (
                      <div
                        className="flex gap-kit-16 items-start"
                        style={{
                          color: 'var(--inyc-primary-text-color-light)',
                          fontFamily: "Primary-Medium",
                        }}
                      >
                        {venueName || googleLocationName}{" "}
                        {!hideEventCount &&
                          venueName &&
                          event.type === "venue" &&
                          event.events?.length > 1 && (
                            <Chip
                              selected
                              color={activeColor}
                              className="shrink-0"
                              onClick={(e) => {
                                if (!onVenueClick) return;
                                e.stopPropagation();
                                onVenueClick();
                              }}
                            >
                              {event.events?.length} event{event.events?.length > 1 && "s"}
                            </Chip>
                          )}
                      </div>
                    ) : (
                      addressLabel
                    )}
                  </div>
                )}
                {eventdistance && (
                  <div className={`text-kit-xs leading-[1.6rem] text-nowrap text-right text-[var(--inyc-secondary-medium)] ml-auto`}>
                    {eventdistance} mi
                  </div>
                )}
              </div>

              <div className="flex w-full min-w-0 mb-kit-8 justify-between overflow-hidden max-w-full items-end gap-kit-8">
            <div
              className="flex-1 min-w-0 overflow-hidden"
              style={{
                WebkitMaskImage: "linear-gradient(to right, black calc(100% - 1.5rem), transparent)",
                maskImage: "linear-gradient(to right, black calc(100% - 1.5rem), transparent)",
              }}
            >
              <div className="flex items-end gap-kit-8 w-max max-w-none">
              {latestEventData?.type === "event" && (
                <span
                  className="inyc-badge is-lg shrink-0"
                  style={{
                    color: categories?.find(
                      (category) => category.id === latestEventData?.category
                    )?.activeColor,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: latestEventData.category
                      ? highlightMatches(
                        latestEventData?.category?.toLowerCase(),
                        latestEventData?._matches,
                        "category"
                      )
                      : "venue",
                  }}
                ></span>
              )}

              {!vmatches && latestEventData?.tags?.length > 0 &&
                [...new Set(latestEventData?.tags.filter(tag => tag && tag.trim() !== ''))]?.map((tag, i) => (
                  <Badge key={i} size="sm" className="shrink-0">
                    {knownServices[tag?.toLowerCase()]?.icon ? (
                      <>
                        <span className="w-[12px] h-[12px] rounded-kit-full flex items-center justify-center overflow-hidden">
                          {loadIcon(knownServices[tag?.toLowerCase()]?.icon, {
                            size: 10,
                          })}
                        </span>
                        {knownServices[tag?.toLowerCase()]?.name}
                      </>
                    ) : (
                      tag?.toLowerCase()
                    )}
                  </Badge>
                ))}

              {/* Add artist name tag */}
              {!vmatches && likedLatestEvent.likes?.includes(user?._id) && (
                <Badge className="shrink-0" aria-label="liked" icon={<AiFillHeart color={activeColor} size={13} />} />
              )}
              </div>
            </div>

            {posterAccount && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  selectAccount(posterAccount);
                }}
                role="button"
                className="inyc-badge shrink-0 ml-auto max-w-[40%] min-w-0 cursor-pointer"
              >
                <span className="truncate">
                  @{posterAccount?.businessName || posterAccount?.username}
                </span>
              </div>
            )}
          </div>

              {/* Venue Type, tags, and amenities - above name for venues */}
              {event?.type === "venue" && vmatches && (
                <div className="flex flex-wrap gap-kit-4 items-center justify-between">
                  <div
                    className="flex flex-wrap gap-kit-4 items-center"
                  >
                    {vmatches && likedLatestEvent.likes?.includes(user?._id) && (
                      <Badge className="" aria-label="liked" icon={<AiFillHeart color={activeColor} size={13} />} />
                    )}
                    {vmatches && latestEventData?.tags?.length > 0 &&
                      [...new Set(latestEventData?.tags.filter(tag => tag && tag.trim() !== ''))]?.map((tag, i) => (
                        <Badge key={i} size="sm">
                          {knownServices[tag?.toLowerCase()]?.icon ? (
                            <>
                              <span className="w-[12px] h-[12px] rounded-kit-full flex items-center justify-center overflow-hidden">
                                {loadIcon(knownServices[tag?.toLowerCase()]?.icon, {
                                  size: 10,
                                })}
                              </span>
                              {knownServices[tag?.toLowerCase()]?.name}
                            </>
                          ) : (
                            tag?.toLowerCase()
                          )}
                        </Badge>
                      ))}
                  </div>
                  <div className="flex ml-auto flex-wrap gap-kit-4 items-center justify-end">

                    {/* Venue attributes (food, alcohol, outdoor) */}
                    {event?.food && (
                      <Badge size="sm" icon={<MdRestaurant size={13} />} />
                    )}
                    {event?.alcohol && (
                      <Badge size="sm" icon={<MdLocalBar size={13} />} />
                    )}
                    {event?.outdoor && (
                      <Badge size="sm" icon={<MdWbSunny size={13} />} />
                    )}


                    {/* Venue Type - larger and more prominent */}
                    {event?.venueType && (
                      <Badge size="sm">{event.venueType?.toLowerCase()}</Badge>
                    )}
                  </div>
                </div>
              )}
              {/* Date and time bubbles on same line */}
              <div className="flex w-full justify-between items-center gap-kit-8 mb-kit-6">
                <div className="flex items-center gap-kit-6 min-w-0">
                  {latestEventDateString && (
                    <Badge strong className="shrink-0">{latestEventDateString}</Badge>
                  )}
                  {latestEventformattedTime && (
                    <Badge strong className="shrink-0">{latestEventformattedTime}</Badge>
                  )}
                </div>
                {ticketBadgeLabel && (
                  <Badge strong className="shrink-0 ml-auto" icon={<TbTicket size={13} className="event-ticket-badge-icon rotate-[-90deg]" />}>
                    {ticketBadgeLabel}
                  </Badge>
                )}
              </div>
              {/* Recurrence text and distance on same line */}
              <div className="flex w-full justify-between items-center">
                {/* {latestEventRruleString && (
                  <div className="text-kit-xs text-[var(--inyc-secondary-medium)] font-['Primary-Regular']">
                    {latestEventRruleString.replace(/^\s*\(|\)$/g, '').trim()}
                  </div>
                )} */}
                {/* {eventdistance && (
                  <div className="text-kit-xs text-[var(--inyc-secondary-medium)] ml-auto">
                    {eventdistance} mi
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoize EventCard to prevent unnecessary re-renders
const MemoizedEventCard = React.memo(EventCard, (prevProps, nextProps) => {
  // Only re-render if data actually changes
  return prevProps.data === nextProps.data;
});

export default MemoizedEventCard;
